import { Link } from "react-router-dom";
import { Icon } from "@/components/Icon";
import { ChevronRight, Clock, Gauge } from "lucide-react";

export function DifficultyBadge({ level }) {
  const map = {
    "Fácil": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    "Médio": "bg-amber-500/15 text-amber-300 border-amber-500/30",
    "Difícil": "bg-red-500/15 text-red-300 border-red-500/30",
  };
  const dot = { "Fácil": "🟢", "Médio": "🟡", "Difícil": "🔴" };
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${map[level] || map["Fácil"]}`}>
      {dot[level] || "🟢"} {level}
    </span>
  );
}

export function DeviceCard({ device }) {
  return (
    <Link to={`/dispositivos/${device.slug}`} data-testid={`device-card-${device.slug}`}
      className="ci-card group relative p-6 flex flex-col overflow-hidden">
      {device.badge && (
        <span className="absolute top-4 right-4 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-amber-400/15 text-amber-300 border border-amber-400/30">
          {device.badge}
        </span>
      )}
      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-600/10 border border-cyan-400/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
        <Icon name={device.icon} className="w-6 h-6 text-cyan-300" />
      </div>
      <h3 className="text-lg font-semibold mb-1">{device.name}</h3>
      <p className="text-sm text-gray-400 leading-relaxed flex-1">{device.description}</p>
      <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-cyan-300 group-hover:gap-2 transition-all">
        Ver tutoriais <ChevronRight className="w-4 h-4" />
      </span>
    </Link>
  );
}

export function AppCard({ app }) {
  return (
    <div data-testid={`app-card-${app.name.toLowerCase().replace(/\s+/g,'-')}`} className="ci-card p-5 flex flex-col">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400/20 to-amber-600/10 border border-amber-400/20 flex items-center justify-center">
          <Icon name={app.icon} className="w-5 h-5 text-amber-300" />
        </div>
        <div>
          <h3 className="font-semibold leading-tight">{app.name}</h3>
          <div className="text-xs text-gray-500 mt-0.5">Dificuldade: {app.difficulty}</div>
        </div>
      </div>
      <p className="text-sm text-gray-400 flex-1">{app.description}</p>
      <div className="flex flex-wrap gap-1.5 mt-3">
        {app.platforms.map((p) => (
          <span key={p} className="text-[11px] px-2 py-0.5 rounded-md bg-white/5 text-gray-300 border border-white/10">{p}</span>
        ))}
      </div>
      {app.tutorial_slug ? (
        <Link to={`/tutorial/${app.tutorial_slug}`} data-testid={`app-install-${app.name.toLowerCase().replace(/\s+/g,'-')}`}
          className="mt-4 inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-cyan-300 hover:text-cyan-200 border border-cyan-400/30 hover:border-cyan-400/60 rounded-lg py-2 transition-colors">
          Ver como instalar <ChevronRight className="w-4 h-4" />
        </Link>
      ) : (
        <span className="mt-4 text-center text-xs text-gray-500 border border-white/5 rounded-lg py-2">Consulte o suporte</span>
      )}
    </div>
  );
}

export function TutorialCard({ tut }) {
  return (
    <Link to={`/tutorial/${tut.slug}`} data-testid={`tutorial-card-${tut.slug}`} className="ci-card p-5 flex flex-col">
      {tut.category && <div className="text-xs font-semibold uppercase tracking-widest text-amber-400 mb-2">{tut.category}</div>}
      <h3 className="font-semibold text-lg leading-tight mb-3 flex-1">{tut.title}</h3>
      <div className="flex items-center gap-3 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-xs text-gray-400"><Clock className="w-3.5 h-3.5" /> {tut.time_estimate}</span>
        <DifficultyBadge level={tut.difficulty} />
      </div>
    </Link>
  );
}

export function HelpBanner({ onWhatsApp, title = "Não encontrou seu dispositivo?", text = "Nossa equipe ajuda você a configurar qualquer aparelho." }) {
  return (
    <div className="ci-card p-8 text-center border-amber-400/20" style={{ background: "linear-gradient(135deg,#131C2E, #1a1508)" }}>
      <h3 className="text-xl sm:text-2xl font-bold mb-2">{title}</h3>
      <p className="text-gray-400 mb-5">{text}</p>
      <button onClick={onWhatsApp} data-testid="help-banner-whatsapp-btn"
        className="ci-btn-gold inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold hover:brightness-105 transition">
        💬 FALAR COM O SUPORTE
      </button>
    </div>
  );
}

export { Gauge };
